import { useEffect, type RefObject } from 'react';

type AnyElement = HTMLElement | null;

export interface UseOnClickOutsideOptions {
  enabled?: boolean;
}

/**
 * Calls `onOutside` when a pointer press happens outside all provided refs.
 * Supports multiple refs (trigger + dropdown + container).
 */
export function useOnClickOutside(
  refs: Array<RefObject<AnyElement>>,
  onOutside: (event: MouseEvent | TouchEvent) => void,
  { enabled = true }: UseOnClickOutsideOptions = {},
) {
  useEffect(() => {
    if (!enabled) return;

    const handler = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      const clickedInsideSomeRef = refs.some((ref) => {
        const el = ref.current;
        return el ? el.contains(target) : false;
      });

      const isIgnored = (target as HTMLElement).closest?.('[data-ignore-outside-click]') != null;

      if (!clickedInsideSomeRef && !isIgnored) {
        onOutside(event);
      }
    };

    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [enabled, onOutside, refs]);
}
